import { useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SupplierForBuyer } from '@dill/dill-shared';
import distributorPlaceholder from '../../../../assets/svg/distributor_placeholder.svg';
import emptyListImg from '../../../../assets/images/emptyList.png';
import { Spinner } from '@phosphor-icons/react';
import {
  getBuyerStatementPeriods,
  getGroupedBuyersSuppliers,
} from '../../../../redux/services/buyersService';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import StatementPeriodRow from './StatementPeriodRow';

const MainBuyerStatementInvoice = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const {
    selectedBuyerSuppliers,
    loadingList: buyersLoadingList,
    loadingStatementPeriods,
    statementPeriods,
  } = useAppSelector((state) => state.buyers);
  const { isPreviewMode } = useAppSelector((state) => state.preview);

  const { user } = useAppSelector((state) => state.auth);

  const supplier = useMemo<SupplierForBuyer | null>(() => {
    if (selectedBuyerSuppliers.length > 0) {
      return selectedBuyerSuppliers[0];
    }
    return null;
  }, [selectedBuyerSuppliers]);

  const isLoading =
    buyersLoadingList.includes('getGroupedBuyersSuppliers') ||
    buyersLoadingList.includes('getBuyerStatementPeriods');

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(getGroupedBuyersSuppliers({ mainBuyerId: selectedMainBuyerId, weeksLimit: 2 }));
    }
  }, [selectedMainBuyerId]);

  useEffect(() => {
    if (selectedMainBuyerId && user?.userMainBuyers && !isPreviewMode) {
      const mainBuyer = user?.userMainBuyers
        .filter((mb) => !mb?.isDeactivated && !mb?.isCODCustomer)
        .find((mb) => mb.id === selectedMainBuyerId);
      if (!mainBuyer) {
        navigate('/companies');
      }
    }
  }, [user?.userMainBuyers, isPreviewMode]);

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(getBuyerStatementPeriods({ mainBuyerId: selectedMainBuyerId }));
    }
  }, [selectedMainBuyerId]);

  const handleNavigate = useCallback(
    (periodId: string) => {
      navigate(`${periodId}`);
    },
    [navigate]
  );

  if (isLoading && supplier) {
    return (
      <div className="h-full w-full p-4 md:p-8">
        <div className="w-full h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-gray-400">
            <Spinner size={28} className="animate-spin" />
            <p className="text-sm">Loading statement data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="h-full w-full p-4 md:p-8">
        <div className="w-full h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <img src={emptyListImg} alt="No data" className="h-32 w-32 opacity-60" />
            <p className="text-sm text-gray-400">No supplier data found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full p-4">
      <div className="w-full h-full overflow-y-scroll pb-4 flex flex-col gap-4">
        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
              {supplier?.logo?.url ? (
                <img className="h-8 w-8 object-contain" src={supplier.logo.url} alt="" />
              ) : (
                <img className="h-6 w-6" src={distributorPlaceholder} alt="" />
              )}
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900">{supplier?.name}</h1>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                Statements
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
              Total Statements
            </p>
            <p className="text-xl md:text-2xl font-semibold text-gray-900 tabular-nums tracking-tight">
              {statementPeriods.length}
            </p>
          </div>
        </div>
        {/* Statement Periods List */}
        {statementPeriods.length === 0 && !loadingStatementPeriods ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 flex flex-col items-center justify-center gap-3">
            <img src={emptyListImg} alt="No statements" className="h-24 w-24 opacity-60" />
            <p className="text-sm text-gray-400">No statement periods available</p>
            <p className="text-xs text-gray-300">
              Statements will appear here when they are generated
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {statementPeriods.map((period) => (
              <StatementPeriodRow
                key={period.id}
                statementData={period}
                onNavigate={() => handleNavigate(period.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MainBuyerStatementInvoice;
